import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { type Messages } from '@empiriecom/mybuy-frontend-api/backend-mybuy-checkout/v1/models/Messages';
import type { CheckoutTargetPage } from '@packages/gql/generated/shopping/graphql';
import { ScrollTopButton } from '@packages/modules/src/ScrollTopButton/ScrollTopButton';
import { parseBackendMessage } from '@packages/modules/src/BackendMessageParser/BackendMessageParser';
import { ProgressBar } from '../ProgressBar/ProgressBar.client';

type CheckoutContainerProps = {
  children?: ReactNode;
  step: number;
  messages?: Messages;
  errorMessage?: string;
  headline?: ReactNode;
  subline?: ReactNode;
  stepSubline?: ReactNode;
  testId?: string;
  rightContent?: ReactNode;
  topNotification?: ReactNode;
  saveAndNavigate?: (
    targetPage: CheckoutTargetPage,
  ) => Promise<CheckoutTargetPage | null | undefined>;
};

export const CheckoutContainer = ({
  children,
  step,
  messages,
  errorMessage,
  headline,
  subline,
  stepSubline,
  // istanbul ignore next
  testId = 'checkout-root',
  rightContent,
  topNotification,
  saveAndNavigate,
}: CheckoutContainerProps) => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="m-0 w-full max-w-screen-xl min-w-0 md:mx-auto md:my-0" data-testid={testId}>
      <ProgressBar activeStep={step} saveAndNavigate={saveAndNavigate} />
      {errorMessage && (
        <Notification severity="error" className="my-1">
          {errorMessage}
        </Notification>
      )}
      {messages &&
        messages.elements.map((message) => (
          <Notification key={message.description} severity={message.level} className="my-1.5">
            {parseBackendMessage(message.description)}
          </Notification>
        ))}
      <div className="mx-auto my-0 text-center md:m-[1.063rem_1.25rem_0.938rem] md:text-left">
        <h1 className="text-size-headline-2xl mt-0">{headline}</h1>
        <div>
          {subline}
          {stepSubline && <div className="text-size-body-sm md:hidden">{stepSubline}</div>}
        </div>
      </div>
      {topNotification && <div className="my-1.5">{topNotification}</div>}
      <div className="flex flex-row justify-between">
        <div className="mt-1.5 w-full max-w-[49.563rem] grow md:mt-0">{children}</div>
        <div className="ml-2 hidden max-w-[20.75rem] grow flex-col gap-2 md:flex">
          {rightContent}
        </div>
      </div>
      {/* Rides above the mobile Buy-Box, whose height `Sticky` publishes as
          --sticky-bottom-height (0 when no Buy-Box is on screen), plus the iOS safe-area
          inset so the button clears the app's translucent tab bar (APPS-6514). */}
      <div
        className="z-app-bar fixed right-[2%] bottom-[calc(2%+var(--spacing-safe-b)+var(--sticky-bottom-height,0px))]"
        id="scrollup-wrapper"
      >
        <ScrollTopButton />
      </div>
    </div>
  );
};
